import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { FormControlComponent, PrimengButtonDirective } from '@web/shared/ui';
import { DateRange, endOfDay, isNil, Nillable } from '@web/utils';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PRIMENG_YY_MM_DD } from '@web/common';
import { AdminMultimediaGridFilter } from '../../models/admin-multimedia-grid-filter';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';

type MultimediaGridFilterFormGroup = FormGroup<{
  query: FormControl<Nillable<string>>;
  createdAt: FormControl<Nillable<[Date, Date]>>;
}>;

@Component({
  selector: 'app-multimedia-grid-filter',
  imports: [
    Button,
    ReactiveFormsModule,
    FormControlComponent,
    InputTextModule,
    DatePicker,
    PrimengButtonDirective,
  ],
  templateUrl: './multimedia-grid-filter.component.html',
  styleUrl: './multimedia-grid-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaGridFilterComponent {
  private readonly builder = inject(FormBuilder);

  public readonly uploadButtonLabel = input<Nillable<string>>(null);
  public readonly uploadButtonDisabled = input<boolean>(false);

  public readonly filterChange = output<AdminMultimediaGridFilter>();
  public readonly uploadClickEvent = output<void>();

  protected readonly form = this.buildForm();
  protected readonly DATE_RANGE_FORMAT = PRIMENG_YY_MM_DD;

  private readonly debounceTime = 300;
  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    this.form.valueChanges
      .pipe(
        debounceTime(this.debounceTime),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.filterChange.emit(this.toData()));
  }

  protected buildForm(): MultimediaGridFilterFormGroup {
    return this.builder.group({
      query: this.builder.control<Nillable<string>>(null),
      createdAt: this.builder.control<Nillable<[Date, Date]>>(null),
    });
  }

  protected handleActionButtonClickEvent(): void {
    this.uploadClickEvent.emit();
  }

  private toData(): AdminMultimediaGridFilter {
    const { query, createdAt } = this.form.getRawValue();

    return new AdminMultimediaGridFilter(
      query,
      this.prepareDateRange(createdAt),
    );
  }

  private prepareDateRange(value: Nillable<Date[]>): Nillable<DateRange> {
    if (isNil(value)) {
      return;
    }

    const [from, to] = value;

    if (isNil(from) && isNil(to)) {
      return;
    }

    return {
      from: from,
      to: endOfDay(to ?? from),
    };
  }
}
